import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Loader, Check, ChevronsUpDown } from 'lucide-react';
import { Combobox } from '@headlessui/react';
import ParticleBackground from './ParticleBackground';
import TopNavBar from './TopNavBar';
import AgentWorkspace from './AgentWorkspace';
import BriefUploadStep from './BriefUploadStep';
import PlanSummaryStep from './PlanSummaryStep';
import AudienceApprovalStep from './AudienceApprovalStep';
import { useOAuth } from '../auth/OAuthContext';
import '../styles/ChatInterfaceNew.css';
import { ADK_API_BASE_URL, createADKSession, ADK_APP_NAME, getSessionInfo, sendADKMessageStreaming, saveSessionConfig, isAuthExpiredError } from '../config/api';
import { PlatformType, WorkflowMode } from '../types';
import { clampPlanningKnob, MAX_PLANNING_KNOB } from '../lib/planningKnobs';

interface ChatInterfaceNewProps {
  onBack: () => void;
  initialQuery?: string;
}

const ChatInterfaceNew: React.FC<ChatInterfaceNewProps> = ({ onBack, initialQuery }) => {
  const { isAuthenticated, userEmail, login, logout } = useOAuth();

  // Configuration state (Step 1)
  const [platform, setPlatform] = useState<PlatformType | null>(null);
  const [workflowMode, setWorkflowMode] = useState<WorkflowMode | null>(null);
  const [historicalAnalysis, setHistoricalAnalysis] = useState(false);
  const [minAudiences, setMinAudiences] = useState<number>(4);
  const [minScenarios, setMinScenarios] = useState<number>(3);
  const [availableFormats, setAvailableFormats] = useState<string[]>([
    'Skippable', 'NonSkip', 'Bumper', 'In-feed', 'Shorts'
  ]);
  const [configComplete, setConfigComplete] = useState(false);

  // Brief upload state (Step 2)
  const [briefRefined, setBriefRefined] = useState(false);

  // Audience approval state (Step 3)
  const [audienceApproved, setAudienceApproved] = useState(false);

  // Define objectives state (Step 4)
  const [summaryApproved, setSummaryApproved] = useState(false);

  // Google Ads state
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState<any[]>([]);
  const [selectedGoogleAdsCustomer, setSelectedGoogleAdsCustomer] = useState<string>('');

  // DV360 state
  const [dv360Partners, setDv360Partners] = useState<any[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('');
  const [dv360Advertisers, setDv360Advertisers] = useState<any[]>([]);
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState<string>('');
  const [loadingDv360Partners, setLoadingDv360Partners] = useState(false);
  const [loadingDv360Advertisers, setLoadingDv360Advertisers] = useState(false);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [advertiserQuery, setAdvertiserQuery] = useState('');

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Step tracking
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(1);

  // Load accounts/partners when platform is selected
  useEffect(() => {
    if (platform === 'google_ads' && googleAdsAccounts.length === 0) {
      loadGoogleAdsAccounts();
    } else if (platform === 'dv360' && dv360Partners.length === 0) {
      loadDv360Partners();
    }
  }, [platform]);

  // Load advertisers when partner is selected
  useEffect(() => {
    if (selectedPartnerId && platform === 'dv360') {
      loadDv360Advertisers(selectedPartnerId);
    }
  }, [selectedPartnerId]);

  // Create session when config is complete
  useEffect(() => {
    if (configComplete && !sessionId) {
      const initializeSession = async () => {
        const { userId } = getSessionInfo();
        if (userId) {
          try {
            const session = await createADKSession(ADK_APP_NAME, userId, platform || undefined);
            setSessionId(session.id);
            console.log('ADK session created:', session.id);

            // Save session configuration for agents to access
            const credsStr = sessionStorage.getItem('oauth_credentials');
            const creds = credsStr ? JSON.parse(credsStr) : {};
            const config = {
              platform,
              workflowMode,
              historicalAnalysis,
              minAudiences,
              minScenarios,
              availableFormats,
              selectedGoogleAdsCustomer,
              selectedAdvertiserId,
              selectedPartnerId,
              oauth_refresh_token: creds.refresh_token || '',
            };

            // This config carries the user's OAuth refresh token, so the
            // endpoint authenticates and checks session ownership.
            await saveSessionConfig(session.id, config);

          } catch (error) {
            if (isAuthExpiredError(error)) {
              // Without this an expired token left the user on a workspace
              // with no session and only a console line to say why.
              console.warn('Session expired; signing out to re-authenticate.');
              logout();
              return;
            }
            console.error('Failed to create ADK session:', error);
          }
        }
      };
      initializeSession();
    }
  }, [configComplete, platform, workflowMode, historicalAnalysis, minAudiences, minScenarios, availableFormats, selectedGoogleAdsCustomer, selectedAdvertiserId, selectedPartnerId]);

  // Update current step based on completion state
  useEffect(() => {
    if (!configComplete) {
      setCurrentStep(1);
    } else if (!briefRefined) {
      setCurrentStep(2);
    } else if (!audienceApproved) {
      setCurrentStep(3);
    } else if (!summaryApproved) {
      setCurrentStep(4);
    } else {
      // Steps 5-7 are handled by AgentWorkspace
      setCurrentStep(5);
    }
  }, [configComplete, briefRefined, audienceApproved, summaryApproved]);

  // Mark steps as complete
  useEffect(() => {
    const completed: number[] = [];
    if (configComplete) completed.push(1);
    if (briefRefined) completed.push(2);
    if (audienceApproved) completed.push(3);
    if (summaryApproved) completed.push(4);
    setCompletedSteps(completed);
  }, [configComplete, briefRefined, audienceApproved, summaryApproved]);

  // Navigation handler to go back to previous steps
  const handleNavigateToStep = (step: number) => {
    if (step < currentStep) {
      // Reset state for steps after the target step
      if (step < 2) {
        setBriefRefined(false);
        setAudienceApproved(false);
        setSummaryApproved(false);
        setConfigComplete(false);
      } else if (step < 3) {
        setAudienceApproved(false);
        setSummaryApproved(false);
        setBriefRefined(false);
      } else if (step < 4) {
        setSummaryApproved(false);
        setAudienceApproved(false);
      } else if (step < 5) {
        setSummaryApproved(false);
      }
    }
  };

  const loadGoogleAdsAccounts = async () => {
    try {
      const credsStr = sessionStorage.getItem('oauth_credentials');
      if (!credsStr) {
        console.error('No OAuth credentials found');
        return;
      }

      const creds = JSON.parse(credsStr);
      const response = await fetch(`${ADK_API_BASE_URL}/oauth/google-ads/accounts`, {
        headers: {
          'Authorization': `Bearer ${creds.access_token}`,
          'X-Refresh-Token': creds.refresh_token || ''
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGoogleAdsAccounts(data || []);
      }
    } catch (error) {
      console.error('Failed to load Google Ads accounts:', error);
    }
  };

  const loadDv360Partners = async () => {
    setLoadingDv360Partners(true);
    try {
      const credsStr = sessionStorage.getItem('oauth_credentials');
      if (!credsStr) {
        console.error('No OAuth credentials found');
        return;
      }

      const creds = JSON.parse(credsStr);
      const response = await fetch(`${ADK_API_BASE_URL}/oauth/dv360/partners`, {
        headers: {
          'Authorization': `Bearer ${creds.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDv360Partners(data || []);
      }
    } catch (error) {
      console.error('Failed to load DV360 partners:', error);
    } finally {
      setLoadingDv360Partners(false);
    }
  };

  const loadDv360Advertisers = async (partnerId: string) => {
    setLoadingDv360Advertisers(true);
    try {
      const credsStr = sessionStorage.getItem('oauth_credentials');
      if (!credsStr) {
        console.error('No OAuth credentials found');
        return;
      }

      const creds = JSON.parse(credsStr);
      const response = await fetch(`${ADK_API_BASE_URL}/oauth/dv360/advertisers?partner_id=${partnerId}`, {
        headers: {
          'Authorization': `Bearer ${creds.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDv360Advertisers(data || []);
      }
    } catch (error) {
      console.error('Failed to load DV360 advertisers:', error);
    } finally {
      setLoadingDv360Advertisers(false);
    }
  };

  const handleLogin = () => {
    const redirectUri = `${window.location.origin}/login`;
    login(redirectUri);
  };

  const handleConfigSubmit = () => {
    const accountId = platform === 'google_ads' ? selectedGoogleAdsCustomer : selectedAdvertiserId;
    if (platform && workflowMode && accountId) {
      setConfigComplete(true);
    }
  };

  // Filter partners and advertisers based on search queries
  const filteredPartners = partnerQuery === ''
    ? dv360Partners
    : dv360Partners.filter((partner) => {
        const searchText = `${partner.display_name} ${partner.partner_id}`.toLowerCase();
        return searchText.includes(partnerQuery.toLowerCase());
      });

  const filteredAdvertisers = advertiserQuery === ''
    ? dv360Advertisers
    : dv360Advertisers.filter((advertiser) => {
        const searchText = `${advertiser.display_name} ${advertiser.advertiser_id}`.toLowerCase();
        return searchText.includes(advertiserQuery.toLowerCase());
      });

  // Render welcome screen
  if (!isAuthenticated) {
    return (
      <div className="app-container">
        <ParticleBackground />
        <TopNavBar
          currentStep={currentStep}
          isAuthenticated={false}
          showSteps={true}
          completedSteps={completedSteps}
        />
        <div className="landing-content">
          <motion.div
            className="welcome-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="welcome-title">Start optimizing your YouTube campaigns!</h1>
            <p className="welcome-subtitle">Create and optimize your next level Youtube Campaigns</p>

            <button className="start-button" onClick={handleLogin}>
              <MessageSquare size={20} />
              <span>Login to Get Started</span>
            </button>
          </motion.div>
        </div>
        <div className="landing-footer">
          Powered by Incubeta Intelligence · Marketing AI Assistant
        </div>
      </div>
    );
  }

  // Render configuration screen (Step 1)
  if (!configComplete) {
    return (
      <div className="app-container">
        <ParticleBackground />
        <TopNavBar
          currentStep={currentStep}
          isAuthenticated={true}
          userEmail={userEmail}
          onLogin={handleLogin}
          onLogout={logout}
          completedSteps={completedSteps}
          onNavigateToStep={handleNavigateToStep}
        />
        <div className="config-content">
          <motion.div
            className="config-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="config-title">Step 1: Configuration</h2>
            <p className="config-subtitle">Set up your campaign planning parameters</p>

            <div className="config-form">
              {/* Platform Selection */}
              <div className="form-group">
                <label>Platform</label>
                <div className="radio-group">
                  <label className="radio-card disabled" title="Google Ads coming soon">
                    <input
                      type="radio"
                      name="platform"
                      value="google_ads"
                      disabled
                    />
                    <span>Google Ads <small style={{ opacity: 0.6, fontSize: '11px' }}>(coming soon)</small></span>
                  </label>
                  <label className={`radio-card ${platform === 'dv360' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="platform"
                      value="dv360"
                      checked={platform === 'dv360'}
                      onChange={(e) => setPlatform(e.target.value as PlatformType)}
                    />
                    <span>DV360</span>
                  </label>
                </div>
              </div>

              {/* Workflow Mode */}
              <div className="form-group">
                <label>Workflow Mode</label>
                <div className="radio-group">
                  <label className={`radio-card ${workflowMode === 'creation' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="mode"
                      value="creation"
                      checked={workflowMode === 'creation'}
                      onChange={(e) => setWorkflowMode(e.target.value as WorkflowMode)}
                    />
                    <span>Creation</span>
                  </label>
                  <label className="radio-card disabled" title="Optimization coming soon">
                    <input
                      type="radio"
                      name="mode"
                      value="optimization"
                      disabled
                    />
                    <span>Optimization <small style={{ opacity: 0.6, fontSize: '11px' }}>(coming soon)</small></span>
                  </label>
                </div>
              </div>

              {/* Account Selection */}
              {platform === 'google_ads' && (
                <div className="form-group">
                  <label>Google Ads Account</label>
                  <select
                    value={selectedGoogleAdsCustomer}
                    onChange={(e) => setSelectedGoogleAdsCustomer(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select an account...</option>
                    {googleAdsAccounts.map((account: any) => (
                      <option key={account.customer_id} value={account.customer_id}>
                        {account.descriptive_name} ({account.customer_id})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {platform === 'dv360' && (
                <>
                  <div className="form-group">
                    <label>DV360 Partner</label>
                    {loadingDv360Partners ? (
                      <div className="loading-indicator">
                        <Loader className="spinner" size={16} />
                        <span>Loading partners...</span>
                      </div>
                    ) : (
                      <Combobox value={selectedPartnerId} onChange={(value) => setSelectedPartnerId(value || '')}>
                        <div className="combobox-wrapper">
                          <Combobox.Input
                            className="form-select combobox-input"
                            displayValue={(partnerId: string) => {
                              const partner = dv360Partners.find(p => p.partner_id === partnerId);
                              return partner ? `${partner.display_name} (${partner.partner_id})` : '';
                            }}
                            onChange={(event) => setPartnerQuery(event.target.value)}
                            placeholder="Search partners..."
                          />
                          <Combobox.Button className="combobox-button">
                            <ChevronsUpDown className="combobox-icon" size={16} />
                          </Combobox.Button>
                          <Combobox.Options className="combobox-options">
                            {filteredPartners.length === 0 && partnerQuery !== '' ? (
                              <div className="combobox-empty">No partners found.</div>
                            ) : (
                              filteredPartners.map((partner: any) => (
                                <Combobox.Option
                                  key={partner.partner_id}
                                  value={partner.partner_id}
                                  className={({ active }) => `combobox-option ${active ? 'active' : ''}`}
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <span className={selected ? 'font-semibold' : ''}>
                                        {partner.display_name} ({partner.partner_id})
                                      </span>
                                      {selected && <Check className="combobox-check" size={16} />}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </div>
                      </Combobox>
                    )}
                  </div>

                  {selectedPartnerId && (
                    <div className="form-group">
                      <label>DV360 Advertiser</label>
                      {loadingDv360Advertisers ? (
                        <div className="loading-indicator">
                          <Loader className="spinner" size={16} />
                          <span>Loading advertisers...</span>
                        </div>
                      ) : (
                        <Combobox value={selectedAdvertiserId} onChange={(value) => setSelectedAdvertiserId(value || '')}>
                          <div className="combobox-wrapper">
                            <Combobox.Input
                              className="form-select combobox-input"
                              displayValue={(advertiserId: string) => {
                                const advertiser = dv360Advertisers.find(a => a.advertiser_id === advertiserId);
                                return advertiser ? `${advertiser.display_name} (${advertiser.advertiser_id})` : '';
                              }}
                              onChange={(event) => setAdvertiserQuery(event.target.value)}
                              placeholder="Search advertisers..."
                            />
                            <Combobox.Button className="combobox-button">
                              <ChevronsUpDown className="combobox-icon" size={16} />
                            </Combobox.Button>
                            <Combobox.Options className="combobox-options">
                              {filteredAdvertisers.length === 0 && advertiserQuery !== '' ? (
                                <div className="combobox-empty">No advertisers found.</div>
                              ) : (
                                filteredAdvertisers.map((advertiser: any) => (
                                  <Combobox.Option
                                    key={advertiser.advertiser_id}
                                    value={advertiser.advertiser_id}
                                    className={({ active }) => `combobox-option ${active ? 'active' : ''}`}
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={selected ? 'font-semibold' : ''}>
                                          {advertiser.display_name} ({advertiser.advertiser_id})
                                        </span>
                                        {selected && <Check className="combobox-check" size={16} />}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))
                              )}
                            </Combobox.Options>
                          </div>
                        </Combobox>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Historical Analysis */}
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={historicalAnalysis}
                    onChange={(e) => setHistoricalAnalysis(e.target.checked)}
                  />
                  <span>Include historical analysis (~40-45 min)</span>
                </label>
              </div>

              {/* Minimum audiences in final campaign — the audience
                  discovery floor. Previously sent as
                  minCombinationScenarios, which drove scenario counts. */}
              <div className="form-group">
                <label>Minimum audiences in final campaign</label>
                <input
                  type="number"
                  className="form-select"
                  min={1}
                  max={MAX_PLANNING_KNOB}
                  value={minAudiences}
                  onChange={(e) => setMinAudiences(clampPlanningKnob(e.target.value))}
                  style={{ width: '100px' }}
                />
                <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                  Discovery tops up from the stored taxonomy if the API suggests fewer.
                </small>
              </div>

              {/* Minimum scenarios in final campaign. Its own control now: the
                  field above used to carry this value while being labelled
                  for audiences, so one number quietly drove the other. */}
              <div className="form-group">
                <label>Minimum scenarios in final campaign</label>
                <input
                  type="number"
                  className="form-select"
                  min={1}
                  max={MAX_PLANNING_KNOB}
                  value={minScenarios}
                  onChange={(e) => setMinScenarios(clampPlanningKnob(e.target.value))}
                  style={{ width: '100px' }}
                />
                <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                  How many scenarios the strategy step must combine.
                </small>
              </div>

              {/* Available ad formats */}
              <div className="form-group">
                <label>Available ad formats</label>
                {[
                  { key: 'Skippable', label: 'Skippable In-stream' },
                  { key: 'NonSkip', label: 'Non-skippable In-stream' },
                  { key: 'Bumper', label: 'Bumper Ads' },
                  { key: 'In-feed', label: 'In-feed Video' },
                  { key: 'Shorts', label: 'Shorts' },
                  { key: 'Audio', label: 'Audio Ads' },
                ].map(({ key, label }) => (
                  <label key={key} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <input
                      type="checkbox"
                      checked={availableFormats.includes(key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAvailableFormats(prev => [...prev, key]);
                        } else {
                          setAvailableFormats(prev => prev.filter(f => f !== key));
                        }
                      }}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>

              <button
                className="continue-button"
                onClick={handleConfigSubmit}
                disabled={
                  !platform ||
                  !workflowMode ||
                  (platform === 'google_ads' && !selectedGoogleAdsCustomer) ||
                  (platform === 'dv360' && !selectedAdvertiserId)
                }
              >
                Continue to Brief Upload
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Render brief upload screen (Step 2 - fullscreen)
  if (configComplete && sessionId && !briefRefined) {
    return (
      <div className="chat-interface">
        <ParticleBackground />
        <TopNavBar
          isAuthenticated={true}
          userEmail={userEmail}
          onLogout={logout}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onNavigateToStep={handleNavigateToStep}
        />
        <div className="chat-container">
          <BriefUploadStep
            sessionId={sessionId}
            platform={platform!}
            onBriefRefined={() => setBriefRefined(true)}
            onLogout={logout}
          />
        </div>
      </div>
    );
  }

  // Render audience approval screen (Step 3 — auto-run)
  if (configComplete && sessionId && briefRefined && !audienceApproved) {
    return (
      <div className="chat-interface">
        <ParticleBackground />
        <TopNavBar
          isAuthenticated={true}
          userEmail={userEmail}
          onLogout={logout}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onNavigateToStep={handleNavigateToStep}
        />
        <div className="chat-container">
          <AudienceApprovalStep
            sessionId={sessionId}
            platform={platform!}
            onAudienceApproved={() => setAudienceApproved(true)}
          />
        </div>
      </div>
    );
  }

  // Render plan summary screen (Step 4 — fullscreen, auto-start).
  // Define Objectives used to live here as a step of its own; it now runs as a
  // background stage of RUN_PLAN_SUMMARY and its verdict appears in the summary.
  if (configComplete && sessionId && briefRefined && audienceApproved && !summaryApproved) {
    return (
      <div className="chat-interface">
        <ParticleBackground />
        <TopNavBar
          isAuthenticated={true}
          userEmail={userEmail}
          onLogout={logout}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onNavigateToStep={handleNavigateToStep}
        />
        <div className="chat-container">
          <PlanSummaryStep
            sessionId={sessionId}
            platform={platform!}
            onSummaryApproved={() => setSummaryApproved(true)}
            onLogout={logout}
          />
        </div>
      </div>
    );
  }

  // Render agent workspace (Steps 5-7)
  if (configComplete && sessionId && briefRefined && audienceApproved && summaryApproved) {
    return (
      <AgentWorkspace
        sessionId={sessionId}
        platform={platform!}
        userEmail={userEmail}
        onLogout={logout}
        selectedGoogleAdsCustomer={selectedGoogleAdsCustomer}
        selectedAdvertiserId={selectedAdvertiserId}
        completedSteps={completedSteps}
        onNavigateToStep={handleNavigateToStep}
        onStepComplete={(step: number) => {
          if (!completedSteps.includes(step)) {
            setCompletedSteps([...completedSteps, step]);
          }
        }}
      />
    );
  }

  return null;
};

export default ChatInterfaceNew;
